import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Wrench, Plus, WarningCircle, PencilSimple, Camera, X, Check, Trash, Image } from '@phosphor-icons/react';

export default function MaintenancePage() {
  const { api } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [form, setForm] = useState({ property_id: '', title: '', description: '', priority: 'medium', assigned_to: '' });
  const [saving, setSaving] = useState(false);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [photos, setPhotos] = useState({});
  const [photoForm, setPhotoForm] = useState({ photo_url: '', photo_type: 'before', caption: '' });

  const load = () => {
    Promise.all([api.get('/maintenance'), api.get('/properties')])
      .then(([t, p]) => { setTickets(t.data); setProperties(p.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingTicket) {
        await api.put(`/maintenance/${editingTicket}`, form);
      } else {
        await api.post('/maintenance', form);
      }
      closeForm();
      load();
    } catch { alert('Error'); } finally { setSaving(false); }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingTicket(null);
    setForm({ property_id: '', title: '', description: '', priority: 'medium', assigned_to: '' });
  };

  const startEdit = (t) => {
    setForm({ property_id: t.property_id, title: t.title, description: t.description || '', priority: t.priority, assigned_to: t.assigned_to || '', status: t.status, notes: t.notes || '' });
    setEditingTicket(t.id);
    setShowForm(true);
  };

  const updateStatus = async (id, status) => {
    await api.put(`/maintenance/${id}/status`, { status });
    load();
  };

  const toggleExpand = async (ticketId) => {
    if (expandedTicket === ticketId) {
      setExpandedTicket(null);
      return;
    }
    setExpandedTicket(ticketId);
    if (!photos[ticketId]) {
      try {
        const { data } = await api.get(`/maintenance/${ticketId}/photos`);
        setPhotos(prev => ({ ...prev, [ticketId]: data }));
      } catch {}
    }
  };

  const addPhoto = async (ticketId) => {
    if (!photoForm.photo_url.trim()) return;
    try {
      const { data } = await api.post(`/maintenance/${ticketId}/photos`, photoForm);
      setPhotos(prev => ({ ...prev, [ticketId]: [...(prev[ticketId] || []), data] }));
      setPhotoForm({ photo_url: '', photo_type: 'before', caption: '' });
    } catch { alert('Error uploading'); }
  };

  const deletePhoto = async (ticketId, photoId) => {
    await api.delete(`/maintenance/${ticketId}/photos/${photoId}`);
    setPhotos(prev => ({ ...prev, [ticketId]: (prev[ticketId] || []).filter(p => p.id !== photoId) }));
  };

  const priorityColor = (p) => {
    if (p === 'high') return 'bg-red-50 text-red-700';
    if (p === 'low') return 'bg-zinc-100 text-zinc-600';
    return 'bg-amber-50 text-amber-700';
  };
  const statusColor = (s) => {
    if (s === 'resolved') return 'bg-emerald-50 text-emerald-700';
    if (s === 'in_progress') return 'bg-blue-50 text-blue-700';
    return 'bg-amber-50 text-amber-700';
  };

  return (
    <div data-testid="maintenance-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Maintenance</h1>
          <p className="text-sm text-zinc-500 mt-1">Track, edit, and verify repairs with before/after photos</p>
        </div>
        <button onClick={() => { closeForm(); setShowForm(true); }} className="px-4 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] flex items-center gap-2" data-testid="add-ticket-btn">
          <Plus weight="bold" size={14} /> New Ticket
        </button>
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/30" onClick={closeForm} />
          <div className="relative bg-white border border-zinc-200 w-full max-w-lg" data-testid="maintenance-form-modal">
            <div className="p-6 border-b border-zinc-200">
              <h2 className="text-lg font-bold text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>{editingTicket ? 'Edit Ticket' : 'New Maintenance Ticket'}</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {!editingTicket && (
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Property</label>
                  <select value={form.property_id} onChange={e => set('property_id', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm bg-white" data-testid="ticket-property-select">
                    <option value="">Select property</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Issue Title</label>
                <input value={form.title} onChange={e => set('title', e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" placeholder="Broken faucet in bathroom" data-testid="ticket-title-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Description</label>
                <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm resize-none" placeholder="Detailed description..." data-testid="ticket-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Priority</label>
                  <select value={form.priority} onChange={e => set('priority', e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm bg-white" data-testid="ticket-priority-select">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Assign To</label>
                  <input value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" placeholder="Vendor name" data-testid="ticket-assignee-input" />
                </div>
              </div>
              {editingTicket && (
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Status</label>
                  <select value={form.status || 'open'} onChange={e => set('status', e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm bg-white" data-testid="ticket-status-select">
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
              )}
              {editingTicket && (
                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1.5 uppercase tracking-wide">Resolution Notes</label>
                  <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={2} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm resize-none" placeholder="What was done to fix the issue..." data-testid="ticket-notes-input" />
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm} className="flex-1 py-2.5 border border-zinc-200 text-sm font-bold text-zinc-700 rounded-sm hover:bg-zinc-50" data-testid="ticket-cancel-btn">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] disabled:opacity-50" data-testid="ticket-save-btn">{saving ? 'Saving...' : editingTicket ? 'Save Changes' : 'Create Ticket'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ticket List */}
      {loading ? <p className="text-sm text-zinc-500">Loading...</p> : tickets.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-16 text-center" data-testid="no-tickets">
          <Wrench size={40} weight="duotone" className="text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>No maintenance tickets</h3>
          <p className="text-sm text-zinc-500">Create a ticket when something needs fixing.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200" data-testid="tickets-list">
          <div className="divide-y divide-zinc-100">
            {tickets.map((t, i) => {
              const ticketPhotos = photos[t.id] || [];
              const beforePhotos = ticketPhotos.filter(p => p.photo_type === 'before');
              const afterPhotos = ticketPhotos.filter(p => p.photo_type === 'after');
              return (
                <div key={t.id} data-testid={`ticket-${i}`}>
                  <div className="px-6 py-4 flex items-center justify-between hover:bg-zinc-50 cursor-pointer" onClick={() => toggleExpand(t.id)}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <WarningCircle size={14} weight="fill" className={t.priority === 'high' ? 'text-red-500' : t.priority === 'medium' ? 'text-amber-500' : 'text-zinc-400'} />
                        <p className="text-sm font-bold text-zinc-900">{t.title}</p>
                        <span className={`text-xs px-2 py-0.5 font-bold ${priorityColor(t.priority)}`}>{t.priority}</span>
                        <span className={`text-xs px-2 py-0.5 font-bold ${statusColor(t.status)}`}>{t.status}</span>
                        {t.source === 'ai_triage' && <span className="text-xs px-2 py-0.5 font-bold bg-violet-50 text-violet-700">AI Triaged</span>}
                      </div>
                      <p className="text-xs text-zinc-500">{t.property_name} {t.assigned_to && `| Assigned: ${t.assigned_to}`}</p>
                    </div>
                    <div className="flex gap-2 ml-4 items-center" onClick={e => e.stopPropagation()}>
                      <button onClick={() => startEdit(t)} className="px-2 py-1.5 border border-zinc-200 text-zinc-500 hover:text-zinc-900 text-xs rounded-sm flex items-center gap-1" data-testid={`ticket-edit-${i}`}>
                        <PencilSimple size={12} /> Edit
                      </button>
                      {t.status === 'open' && (
                        <button onClick={() => updateStatus(t.id, 'in_progress')} className="px-3 py-1.5 bg-[#0052FF] text-white text-xs font-bold rounded-sm" data-testid={`ticket-progress-${i}`}>In Progress</button>
                      )}
                      {(t.status === 'open' || t.status === 'in_progress') && (
                        <button onClick={() => updateStatus(t.id, 'resolved')} className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-sm" data-testid={`ticket-resolve-${i}`}>Resolve</button>
                      )}
                    </div>
                  </div>

                  {/* Expanded: Description + Before/After Photos */}
                  {expandedTicket === t.id && (
                    <div className="px-6 pb-5 bg-zinc-50 border-t border-zinc-100" data-testid={`ticket-expanded-${i}`}>
                      {t.description && (
                        <div className="pt-3 mb-4">
                          <p className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-1">Description</p>
                          <p className="text-sm text-zinc-600">{t.description}</p>
                        </div>
                      )}
                      {t.notes && (
                        <div className="mb-4">
                          <p className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-1">Resolution Notes</p>
                          <p className="text-sm text-zinc-600">{t.notes}</p>
                        </div>
                      )}

                      {/* Before/After Photos Grid */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs font-bold text-red-600 uppercase tracking-wide mb-2 flex items-center gap-1"><Camera size={12} /> Before ({beforePhotos.length})</p>
                          {beforePhotos.length > 0 ? (
                            <div className="space-y-2">
                              {beforePhotos.map(p => (
                                <div key={p.id} className="relative border border-zinc-200 rounded-sm overflow-hidden bg-white">
                                  <img src={p.photo_url} alt={p.caption} className="w-full h-28 object-cover" />
                                  <div className="p-2 flex items-center justify-between">
                                    <p className="text-xs text-zinc-600 truncate">{p.caption || 'Before photo'}</p>
                                    <button onClick={() => deletePhoto(t.id, p.id)} className="text-zinc-400 hover:text-red-600"><Trash size={12} /></button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="border border-dashed border-zinc-300 rounded-sm p-4 text-center">
                              <Image size={20} weight="duotone" className="text-zinc-300 mx-auto mb-1" />
                              <p className="text-xs text-zinc-400">No before photos</p>
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-emerald-600 uppercase tracking-wide mb-2 flex items-center gap-1"><Check size={12} /> After ({afterPhotos.length})</p>
                          {afterPhotos.length > 0 ? (
                            <div className="space-y-2">
                              {afterPhotos.map(p => (
                                <div key={p.id} className="relative border border-zinc-200 rounded-sm overflow-hidden bg-white">
                                  <img src={p.photo_url} alt={p.caption} className="w-full h-28 object-cover" />
                                  <div className="p-2 flex items-center justify-between">
                                    <p className="text-xs text-zinc-600 truncate">{p.caption || 'After photo'}</p>
                                    <button onClick={() => deletePhoto(t.id, p.id)} className="text-zinc-400 hover:text-red-600"><Trash size={12} /></button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="border border-dashed border-zinc-300 rounded-sm p-4 text-center">
                              <Image size={20} weight="duotone" className="text-zinc-300 mx-auto mb-1" />
                              <p className="text-xs text-zinc-400">No after photos</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Add Photo Form */}
                      <div className="bg-white border border-zinc-200 p-3 rounded-sm">
                        <p className="text-xs font-bold text-zinc-700 mb-2 uppercase tracking-wide">Add Photo</p>
                        <div className="flex gap-2">
                          <input value={photoForm.photo_url} onChange={e => setPhotoForm(f => ({...f, photo_url: e.target.value}))} className="flex-1 px-2.5 py-2 border border-zinc-200 text-xs rounded-sm" placeholder="Image URL (https://...)" data-testid={`photo-url-${i}`} />
                          <select value={photoForm.photo_type} onChange={e => setPhotoForm(f => ({...f, photo_type: e.target.value}))} className="px-2.5 py-2 border border-zinc-200 text-xs rounded-sm bg-white w-24" data-testid={`photo-type-${i}`}>
                            <option value="before">Before</option>
                            <option value="after">After</option>
                          </select>
                          <input value={photoForm.caption} onChange={e => setPhotoForm(f => ({...f, caption: e.target.value}))} className="w-32 px-2.5 py-2 border border-zinc-200 text-xs rounded-sm" placeholder="Caption" data-testid={`photo-caption-${i}`} />
                          <button onClick={() => addPhoto(t.id)} className="px-3 py-2 bg-[#0052FF] text-white text-xs font-bold rounded-sm flex items-center gap-1" data-testid={`photo-add-${i}`}>
                            <Camera size={12} /> Add
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
